/* eslint-disable max-lines-per-function */
import React, { useState, useCallback, useEffect } from 'react'
import { Container, Grid } from '@mui/material'
import {
  DndContext,
  closestCenter,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  useSortable,
  rectSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import Layout from '~/components/Layout'
import InternalHeader from '~/components/Layout/components/InternalHeader'
import InternalContent from '~/components/Layout/components/InternalContent'
import CustomDashboardFilters from '../components/CustomDashboardFilters'
import CustomDashboardAddChartSection from '../components/CustomDashboardAddChartSection'
import CustomDashboardChartWidget from '../components/CustomDashboardChartWidget'
import CustomDashboardChartsList from '../components/CustomDashboardChartsList'
import { useLang } from '~/hooks/useLang'
import {
  IChartConfig,
  IStatisticsFilters,
  DataCombinationType,
  ChartType,
  StatisticsDataType,
  IDispatchByStatus,
} from '~/interfaces/IStatistics'

interface SortableChartItemProps {
  readonly chart: IChartConfig
  readonly chartIndex: number
  readonly chartData: StatisticsDataType | null
  readonly loading: boolean
  readonly onRemove: (id: string) => void
}

function SortableChartItem({
  chart,
  chartIndex,
  chartData,
  loading,
  onRemove,
}: SortableChartItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: chart.id })

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const isFullWidth =
    chart.chartType === ChartType.NUMERIC ||
    chart.chartType === ChartType.COLUMN ||
    chart.chartType === ChartType.LINE

  return (
    <Grid
      item
      xs={12}
      md={isFullWidth ? 12 : 6}
      lg={isFullWidth ? 12 : 6}
      ref={setNodeRef}
      style={style}
    >
      <CustomDashboardChartWidget
        id={chart.id}
        chartType={chart.chartType}
        dataCombination={chart.dataCombination}
        data={chartData}
        loading={loading}
        onRemove={onRemove}
        chartIndex={chartIndex}
        dragHandleAttributes={attributes}
        dragHandleListeners={listeners}
      />
    </Grid>
  )
}
import {
  getDispatchByStatus,
  getDeviceByStatus,
  getDispatchByType,
  getDispatchByTypeSubtype,
  getDispatchByClosedStatus,
  getDispatchByDispatchGroup,
  getDispatchByCity,
  getDispatchByDistrict,
  getDispatchByDevice,
  getDispatchesByHours,
  getDispatchesByWeekday,
} from '~/services/statistics'
import notify from '~/utils/notify'

export default function CustomDashboard() {
  const { translate } = useLang()
  const [filters, setFilters] = useState<IStatisticsFilters | null>(null)
  const [charts, setCharts] = useState<readonly IChartConfig[]>([])

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      setCharts((prev) => {
        const oldIndex = prev.findIndex((chart) => chart.id === active.id)
        const newIndex = prev.findIndex((chart) => chart.id === over.id)
        return arrayMove([...prev], oldIndex, newIndex)
      })
    }
  }
  const [chartsData, setChartsData] = useState<
    Record<string, StatisticsDataType | null>
  >({})
  const [loadingCharts, setLoadingCharts] = useState<Record<string, boolean>>(
    {}
  )
  const [filtersVisible] = useState(true)
  const [hasGeneratedCharts, setHasGeneratedCharts] = useState(false)

  const fetchChartData = useCallback(
    async (chartConfig: IChartConfig, currentFilters?: IStatisticsFilters) => {
      const chartFilters = currentFilters || filters
      if (!chartFilters) return

      setLoadingCharts((prev) => ({ ...prev, [chartConfig.id]: true }))

      try {
        let data: StatisticsDataType

        switch (chartConfig.dataCombination) {
          case DataCombinationType.DISPATCH_BY_STATUS:
            data = await getDispatchByStatus(chartFilters)
            data = (data as readonly IDispatchByStatus[]).filter(
              (item) => item.id !== 8 && item.id !== 10
            )
            break
          case DataCombinationType.DEVICE_BY_STATUS:
            data = await getDeviceByStatus(chartFilters)
            break
          case DataCombinationType.DISPATCH_BY_TYPE:
            data = await getDispatchByType(chartFilters)
            break
          case DataCombinationType.DISPATCH_BY_SUBTYPE:
            data = await getDispatchByTypeSubtype(chartFilters)
            break
          case DataCombinationType.DISPATCH_BY_CLOSED_STATUS:
            data = await getDispatchByClosedStatus(chartFilters)
            break
          case DataCombinationType.DISPATCH_BY_DISPATCH_GROUP:
            data = await getDispatchByDispatchGroup(chartFilters)
            break
          case DataCombinationType.DISPATCH_BY_CITY:
            data = await getDispatchByCity(chartFilters)
            break
          case DataCombinationType.DISPATCH_BY_DISTRICT:
            data = await getDispatchByDistrict(chartFilters)
            break
          case DataCombinationType.DISPATCH_BY_DEVICE:
            data = await getDispatchByDevice(chartFilters)
            break
          case DataCombinationType.DISPATCHES_BY_HOURS:
            data = await getDispatchesByHours(chartFilters)
            break
          case DataCombinationType.DISPATCHES_BY_WEEKDAY:
            data = await getDispatchesByWeekday(chartFilters)
            break
          default:
            throw new Error('Tipo de combinação de dados não suportado')
        }

        setChartsData((prev) => ({ ...prev, [chartConfig.id]: data }))
      } catch (error) {
        console.error('Erro ao buscar dados do gráfico:', error)
        notify({
          message: translate('Erro ao carregar dados do gráfico'),
          type: 'error',
        })
        setChartsData((prev) => ({ ...prev, [chartConfig.id]: null }))
      } finally {
        setLoadingCharts((prev) => ({ ...prev, [chartConfig.id]: false }))
      }
    },
    [filters, translate]
  )

  useEffect(() => {
    if (filters && charts.length > 0 && hasGeneratedCharts) {
      // Quando os filtros mudam, atualiza todos os gráficos existentes
      charts.forEach((chart) => {
        fetchChartData(chart)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, hasGeneratedCharts])

  const handleFiltersChange = (newFilters: IStatisticsFilters) => {
    setFilters(newFilters)
    // Ao aplicar filtros, gera os gráficos
    if (charts.length > 0) {
      setHasGeneratedCharts(true)
    }
  }

  const clearFilters = () => {
    setFilters(null)
    setHasGeneratedCharts(false)
    setChartsData({})
  }

  const handleAddChart = (
    dataCombination: DataCombinationType,
    chartType: ChartType
  ) => {
    const newChart: IChartConfig = {
      id: `chart-${Date.now()}`,
      dataCombination,
      chartType,
      filters: filters || ({} as IStatisticsFilters),
      position: charts.length,
    }

    setCharts((prev) => [...prev, newChart])
    
    if (filters && hasGeneratedCharts) {
      fetchChartData(newChart, filters)
      notify({
        message: translate('Novo gráfico adicionado com sucesso!'),
        type: 'success',
      })
    }
  }

  const handleRemoveChart = (id: string) => {
    setCharts((prev) => prev.filter((chart) => chart.id !== id))
    setChartsData((prev) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [id]: removed, ...rest } = prev
      return rest
    })
    setLoadingCharts((prev) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [id]: removed, ...rest } = prev
      return rest
    })
    if (charts.length === 1) {
      setHasGeneratedCharts(false)
    }
  }

  return (
    <Layout marginLayout={true}>
      <InternalHeader title={translate('Operational panels')} />
      <Container maxWidth={false} disableGutters>
        <InternalContent
          title={translate('Adicionar Gráfico')}
          sxFont={{ fontWeight: 'bold', fontSize: 18 }}
          alreadyExpand={true}
        >
          <CustomDashboardAddChartSection
            onAdd={handleAddChart}
            disabled={false}
            hidden={true}
            existingCharts={charts}
          />
        </InternalContent>
        {/* Lista de gráficos adicionados (antes de aplicar filtros) */}
        {charts.length > 0 && !hasGeneratedCharts && (
          <CustomDashboardChartsList
            charts={charts}
            onRemove={handleRemoveChart}
          />
        )}
        <InternalContent
          title={translate('Filtros')}
          sxFont={{ fontWeight: 'bold', fontSize: 18 }}
          alreadyExpand={filtersVisible}
        >
          <CustomDashboardFilters
            onFiltersChange={handleFiltersChange}
            hidden={filtersVisible}
            clearFilters={clearFilters}
          />
        </InternalContent>

        {/* Gráficos renderizados (após aplicar filtros) */}
        {charts.length > 0 && hasGeneratedCharts && (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={charts.map((chart) => chart.id)}
              strategy={rectSortingStrategy}
            >
              <Grid container spacing={3}>
                {charts.map((chart, chartIndex) => (
                  <SortableChartItem
                    key={chart.id}
                    chart={chart}
                    chartIndex={chartIndex}
                    chartData={chartsData[chart.id] || null}
                    loading={loadingCharts[chart.id] || false}
                    onRemove={handleRemoveChart}
                  />
                ))}
              </Grid>
            </SortableContext>
          </DndContext>
        )}
      </Container>
    </Layout>
  )
}
